import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, signal } from '@angular/core';
import { FormArray, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AutoAuditDirective } from '../../../shared/directives/auto-audit.directive';
import { AuditService } from '../../../core/services/audit/audit.service';
import { LatestPerFieldMap } from '../../../core/models/audit.model';
import { faPlus, faTrash, faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';
import { CommonModule } from '@angular/common';
import { FiltersService } from '../../../core/services/declarations/filters.service';
import { ClientWorkTeamsService, ClientWorkTeamCreateDTO, ClientWorkTeamResponse, CollaboratorResponse } from '../../../core/services/clients/client-work-teams.service';
import { DEPARTMENTS_TYPES } from '../../../core/constants/client.constants';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-work-team-form',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CalendarModule, InputTextModule, InputTextareaModule, ButtonModule, DropdownModule, MultiSelectModule, FontAwesomeModule, CommonModule, AutoAuditDirective],
  templateUrl: './work-team-form.component.html',
  styleUrl: './work-team-form.component.scss'
})
export class WorkTeamFormComponent implements OnChanges {
  @Input() set clientId(value: any) {
    this._clientId = value;
    if (value && this.usersLoaded) {
      this.loadWorkTeamData();
    }
  }
  get clientId(): any {
    return this._clientId;
  }
  private _clientId: any;
  @Input() snapshotData: any | null = null;
  @Output() formIsDirty = new EventEmitter<boolean>();

  faPlus = faPlus;
  faTrash = faTrash;
  faFloppyDisk = faFloppyDisk;

  workTeamForm = this.fb.group({
    // Fechas de alta
    totalStartDate: [null as Date | null],
    accountingRecurrentStartDate: [null as Date | null],
    accountingStartDate: [null as Date | null],
    laborRecurrentStartDate: [null as Date | null],
    laborStartDate: [null as Date | null],
    fiscalRecurrentStartDate: [null as Date | null],
    fiscalStartDate: [null as Date | null],
    observations: [''],

    // Responsables y colaboradores
    accountingResponsible: [null as number | null],
    accountingCollaborators: [[] as number[]],
    laborResponsible: [null as number | null],
    laborCollaborators: [[] as number[]],
    fiscalResponsible: [null as number | null],
    fiscalCollaborators: [[] as number[]],
    invoicingResponsible: [null as number | null],
    
    // IDs para persistencia
    id: [null as number | null],
    client: [null as number | null]
  });

  // Listas de usuarios por departamento
  accountingUsers: any[] = [];
  laborUsers: any[] = [];
  fiscalUsers: any[] = [];
  invoicingUsers: any[] = [];
  departmentsTypes = DEPARTMENTS_TYPES;
  usersLoaded = false;
  isSaved = signal<boolean>(false);
  fieldAuditData: LatestPerFieldMap = {};

  constructor(
    private fb: FormBuilder,
    private filtersService: FiltersService,
    private workTeamsService: ClientWorkTeamsService,
    private auditService: AuditService,
    private authService: AuthService,
  ) { }

  async ngOnInit() {
    await this.loadUsers();
    if (!this.snapshotData) {
      await this.loadWorkTeamData();
    }
    this.setupDateListeners();
    
    // Track form changes for dirty state
    this.workTeamForm.valueChanges.subscribe(() => {
      this.formIsDirty.emit(true);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['snapshotData']) {
      if (this.snapshotData) {
        const parseDate = (v: any) => v ? new Date(v) : null;
        const d = this.snapshotData;
        this.workTeamForm.patchValue({
          totalStartDate: parseDate(d.totalStartDate),
          accountingRecurrentStartDate: parseDate(d.recurrentServicesAccountingStartDate),
          accountingStartDate: parseDate(d.accountingStartDate),
          laborRecurrentStartDate: parseDate(d.recurrentServicesLaborStartDate),
          laborStartDate: parseDate(d.laborStartDate),
          fiscalRecurrentStartDate: parseDate(d.recurrentServicesFiscalStartDate),
          fiscalStartDate: parseDate(d.fiscalStartDate),
          observations: d.observations ?? '',
          accountingResponsible: d.accountingResponsible?.id ?? d.accountingResponsible ?? null,
          laborResponsible: d.laborResponsible?.id ?? d.laborResponsible ?? null,
          fiscalResponsible: d.fiscalResponsible?.id ?? d.fiscalResponsible ?? null,
          invoicingResponsible: d.billingResponsible?.id ?? d.billingResponsible ?? null,
          accountingCollaborators: d.accountingCollaborators || [],
          laborCollaborators: d.laborCollaborators || [],
          fiscalCollaborators: d.fiscalCollaborators || [],
        }, { emitEvent: false });
        this.workTeamForm.disable({ emitEvent: false });
      } else if (!changes['snapshotData'].firstChange) {
        this.workTeamForm.enable({ emitEvent: false });
        void this.loadWorkTeamData();
      }
    }
  }

  setupDateListeners() {
    // Listener para fecha recurrente contable
    this.workTeamForm.get('accountingRecurrentStartDate')?.valueChanges.subscribe(value => {
      if (value && !this.workTeamForm.get('accountingStartDate')?.value) {
        this.workTeamForm.get('accountingStartDate')?.setValue(value);
      }
    });

    // Listener para fecha recurrente laboral
    this.workTeamForm.get('laborRecurrentStartDate')?.valueChanges.subscribe(value => {
      if (value && !this.workTeamForm.get('laborStartDate')?.value) {
        this.workTeamForm.get('laborStartDate')?.setValue(value);
      }
    });

    // Listener para fecha recurrente fiscal
    this.workTeamForm.get('fiscalRecurrentStartDate')?.valueChanges.subscribe(value => {
      if (value && !this.workTeamForm.get('fiscalStartDate')?.value) {
        this.workTeamForm.get('fiscalStartDate')?.setValue(value);
      }
    });
  }

  async loadUsers() {
    try {
      const user = this.authService.getUser();
      const allUsers = await this.filtersService.getUsers();
      
      // Filtrar usuarios por departamento específico para cada área, excluyendo admin y GENERAL
      this.accountingUsers = allUsers.filter(u => 
        u.username !== 'admin' && 
        u.department === 'ACCOUNTING'
      );
      
      this.laborUsers = allUsers.filter(u => 
        u.username !== 'admin' && 
        u.department === 'LABOR'
      );
      
      this.fiscalUsers = allUsers.filter(u => 
        u.username !== 'admin' && 
        u.department === 'FISCAL'
      );
      
      // Para facturación, usar solo usuarios BILLING, excluyendo admin y GENERAL
      this.invoicingUsers = allUsers.filter(u => 
        u.username !== 'admin' && 
        u.department === 'BILLING'
      );
      
      this.usersLoaded = true;
      
    } catch (error) {
      console.error('Error loading users:', error);
      this.accountingUsers = [];
      this.laborUsers = [];
      this.fiscalUsers = [];
      this.invoicingUsers = [];
      this.usersLoaded = true; // Marcar como cargado incluso en caso de error
    }
  }

  onFormChange() {
    this.formIsDirty.emit(true);
    this.syncServiceDates();
  }

  getSelectedCollaborators(formControlName: 'accountingCollaborators' | 'laborCollaborators' | 'fiscalCollaborators') {
    const selectedIds = this.workTeamForm.get(formControlName)?.value || [];
    const usersList = this.getUsersListByFormControl(formControlName);
    return Array.isArray(selectedIds) ? usersList.filter(user => selectedIds.includes(user.id)) : [];
  }

  private getUsersListByFormControl(formControlName: string) {
    switch (formControlName) {
      case 'accountingCollaborators':
        return this.accountingUsers;
      case 'laborCollaborators':
        return this.laborUsers;
      case 'fiscalCollaborators':
        return this.fiscalUsers;
      default:
        return [];
    }
  }

  syncServiceDates() {
    // Sincronizar fechas de servicios con fechas recurrentes si no están definidas
    const form = this.workTeamForm;
    
    // Área Contable
    if (form.get('accountingRecurrentStartDate')?.value && !form.get('accountingStartDate')?.value) {
      form.get('accountingStartDate')?.setValue(form.get('accountingRecurrentStartDate')?.value);
    }
    
    // Área Laboral
    if (form.get('laborRecurrentStartDate')?.value && !form.get('laborStartDate')?.value) {
      form.get('laborStartDate')?.setValue(form.get('laborRecurrentStartDate')?.value);
    }
    
    // Área Fiscal
    if (form.get('fiscalRecurrentStartDate')?.value && !form.get('fiscalStartDate')?.value) {
      form.get('fiscalStartDate')?.setValue(form.get('fiscalRecurrentStartDate')?.value);
    }
  }

  /** Guarda de envío: un doble clic crearía duplicados. */
  isSubmitting = false;
  submitError = '';

  async save() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.submitError = '';
    try {
      await this.doSave();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo guardar el equipo de trabajo. Inténtalo de nuevo.';
    } finally {
      this.isSubmitting = false;
    }
  }

  private async doSave() {
    if (this.workTeamForm.valid) {
      try {
        const formData = this.workTeamForm.value;
        // Formatear fechas para el backend
        const formatDate = (date: any) => {
          if (!date) return null;
          
          let dateObj: Date;
          if (date instanceof Date) {
            dateObj = date;
          } else if (typeof date === 'string') {
            dateObj = new Date(date);
          } else {
            return null;
          }
          
          // Check if date is valid before calling toISOString()
          if (isNaN(dateObj.getTime())) {
            console.warn('Invalid date detected:', date);
            return null;
          }
          
          return dateObj.toISOString();
        };

        const workTeamData: ClientWorkTeamCreateDTO = {
          id: formData.id && formData.id > 0 ? formData.id : undefined,
          client: this.clientId,
          totalStartDate: formatDate(formData.totalStartDate),
          recurrentServicesAccountingStartDate: formatDate(formData.accountingRecurrentStartDate),
          accountingStartDate: formatDate(formData.accountingStartDate),
          recurrentServicesLaborStartDate: formatDate(formData.laborRecurrentStartDate),
          laborStartDate: formatDate(formData.laborStartDate),
          recurrentServicesFiscalStartDate: formatDate(formData.fiscalRecurrentStartDate),
          fiscalStartDate: formatDate(formData.fiscalStartDate),
          observations: (formData.observations && String(formData.observations).trim()) || null,
          accountingResponsible: formData.accountingResponsible ? Number(formData.accountingResponsible) : null,
          laborResponsible: formData.laborResponsible ? Number(formData.laborResponsible) : null,
          fiscalResponsible: formData.fiscalResponsible ? Number(formData.fiscalResponsible) : null,
          billingResponsible: formData.invoicingResponsible ? Number(formData.invoicingResponsible) : null
        };

        const result = await this.workTeamsService.createOrUpdate(workTeamData);
        
        
        // Actualizar la ID en el formulario si se creó un nuevo equipo
        if (result && result.id && !formData.id) {
          this.workTeamForm.patchValue({ id: result.id });
        }
        
        // Si hay colaboradores, actualizarlos por área
        if (result && result.id) {
          await this.updateCollaborators(result.id);
        }

        await this.loadWorkTeamData();

        this.formIsDirty.emit(false);
        this.isSaved.set(true);
        setTimeout(() => this.isSaved.set(false), 3000);
      } catch (error) {
        console.error('Error saving work team:', error);
        throw error; // lo recoge el guardado protegido y lo muestra
      }
    }
  }

  private async updateCollaborators(workTeamId: number) {
    const formData = this.workTeamForm.value;
    
    try {
      // También con la lista vacía: quitar a todos los colaboradores de un área es un cambio válido.
      // Actualizar colaboradores contables
      if (Array.isArray(formData.accountingCollaborators)) {
        await this.workTeamsService.setCollaboratorsForArea(
          workTeamId, 
          'ACCOUNTING', 
          formData.accountingCollaborators
        );
      }

      // Actualizar colaboradores laborales
      if (Array.isArray(formData.laborCollaborators)) {
        await this.workTeamsService.setCollaboratorsForArea(
          workTeamId, 
          'LABOR', 
          formData.laborCollaborators
        );
      }

      // Actualizar colaboradores fiscales
      if (Array.isArray(formData.fiscalCollaborators)) {
        await this.workTeamsService.setCollaboratorsForArea(
          workTeamId, 
          'FISCAL', 
          formData.fiscalCollaborators
        );
      }
    } catch (error) {
      console.error('Error updating collaborators:', error);
    }
  }

  async loadWorkTeamData() {
    if (!this.clientId) {
      console.warn('No clientId available for loading work team data');
      return;
    }
    
    try {
      const workTeamData = await this.workTeamsService.getByClient(this.clientId);
      
      if (workTeamData) {
        
        // Función para convertir string a Date
        const parseDate = (dateString: string | null | undefined): Date | null => {
          if (!dateString) return null;
          try {
            return new Date(dateString);
          } catch (error) {
            console.warn('Error parsing date:', dateString, error);
            return null;
          }
        };

        // Cargar datos básicos del equipo de trabajo
        this.workTeamForm.patchValue({
          id: workTeamData.id,
          totalStartDate: parseDate(workTeamData.totalStartDate),
          accountingRecurrentStartDate: parseDate(workTeamData.recurrentServicesAccountingStartDate),
          accountingStartDate: parseDate(workTeamData.accountingStartDate),
          laborRecurrentStartDate: parseDate(workTeamData.recurrentServicesLaborStartDate),
          laborStartDate: parseDate(workTeamData.laborStartDate),
          fiscalRecurrentStartDate: parseDate(workTeamData.recurrentServicesFiscalStartDate),
          fiscalStartDate: parseDate(workTeamData.fiscalStartDate),
          accountingResponsible: workTeamData.accountingResponsible?.id,
          laborResponsible: workTeamData.laborResponsible?.id,
          fiscalResponsible: workTeamData.fiscalResponsible?.id,
          invoicingResponsible: workTeamData.billingResponsible?.id,
          observations: (workTeamData as any).observations ?? ''
        });

        // Cargar colaboradores desde la respuesta del backend
        this.workTeamForm.patchValue({
          accountingCollaborators: workTeamData.accountingCollaborators || [],
          laborCollaborators: workTeamData.laborCollaborators || [],
          fiscalCollaborators: workTeamData.fiscalCollaborators || []
        });
      } else {
        // Inicializar formulario con valores por defecto
        this.workTeamForm.patchValue({
          client: this.clientId
        });
      }
    } catch (error) {
      console.error('Error loading work team data:', error);
      this.workTeamForm.patchValue({
        client: this.clientId
      });
    }

    try {
      this.fieldAuditData = await this.auditService.getLatestPerField(this.clientId, 'client_work_team');
    } catch {
      this.fieldAuditData = {};
    }
  }

  async loadCollaborators(workTeamId: number) {
    try {
      
      // Cargar colaboradores por área
      const [accountingCollaborators, laborCollaborators, fiscalCollaborators] = await Promise.all([
        this.workTeamsService.getCollaboratorsByArea(workTeamId, 'ACCOUNTING'),
        this.workTeamsService.getCollaboratorsByArea(workTeamId, 'LABOR'),
        this.workTeamsService.getCollaboratorsByArea(workTeamId, 'FISCAL')
      ]);

      // Actualizar el formulario con los colaboradores
      this.workTeamForm.patchValue({
        accountingCollaborators: accountingCollaborators?.map(c => c.userId) || [],
        laborCollaborators: laborCollaborators?.map(c => c.userId) || [],
        fiscalCollaborators: fiscalCollaborators?.map(c => c.userId) || []
      });
    } catch (error) {
      console.error('Error loading collaborators:', error);
      // En caso de error, inicializar con arrays vacíos
      this.workTeamForm.patchValue({
        accountingCollaborators: [],
        laborCollaborators: [],
        fiscalCollaborators: []
      });
    }
  }
}
